import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CellClickedEvent, ColDef, ColumnState, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { debounceTime, filter, interval, Subject, takeUntil } from 'rxjs';
import { AGTableBase } from '../../../shared/components/table/ag-table-base';
import { CellClickEvent } from '../../../shared/components/table/table.component';
import {
  PhoneReport,
  PhoneReportCollection,
  PhoneReportConnectivityInfo,
  PhoneReportConnectivityStatus,
  PhoneReportOutcomeOutput,
  PhoneReportOutcomeResolutionEnum,
  PhoneReportTestType,
} from '../../../shared/models/phone-report.model';
import { Role } from '../../../shared/models/user.model';
import { BrowserUtils } from '../../../shared/services/browser-utils';
import { DlrRecStatusRenderUtils } from '../../../shared/services/dlr-rec-status-render-utils';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhoneReportConnectivityUtils } from '../../../shared/services/phone-report-connectivity-utils';
import { PhoneReportUtils } from '../../../shared/services/phone-report-utils';
import { PhoneReportActionData, PhoneReportRequestParams, PhoneReportService } from '../../../shared/services/phone-report.service';
import { UsersService } from '../../../shared/services/users.service';
import { PhonesReportsTableActionsComponent } from './phones-reports-table-actions/phones-reports-table-actions.component';

declare const moment: any;

@Component({
  standalone: false,
  selector: 'app-phone-reports-table',
  templateUrl: './phone-reports-table.component.html',
})
export class PhoneReportsTableComponent extends AGTableBase implements OnInit, OnDestroy {
  @Output() actions = new EventEmitter<PhoneReportActionData>();
  @Output() afterDelete = new EventEmitter<void>();
  @Output() changeBatch = new EventEmitter<PhoneReport[]>();

  @ViewChild('deleteModalTpl', { read: TemplateRef, static: false }) deleteModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('connectivityModalTpl', { read: TemplateRef, static: false }) connectivityModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('phoneReportsTestResultModalTpl', { read: TemplateRef, static: false }) phoneReportsTestResultModalTpl: TemplateRef<HTMLTemplateElement>;
  @ViewChild('phoneReportsReportOutcomeModalTpl', { read: TemplateRef, static: false })
  phoneReportsReportOutcomeModalTpl: TemplateRef<HTMLTemplateElement>;

  private destroy$ = new Subject<void>();

  requestParams = new PhoneReportRequestParams();
  loading = true;
  batchSelectedIds = new Set<number>();

  reportOutcomeModalInfo: PhoneReport | null = null;
  connectivityModalInfo: {
    report: PhoneReport;
    connectivityInfo: PhoneReportConnectivityInfo;
    isAdmin: boolean;
  } | null = null;
  testResultModalReportId: number | null = null;

  // ng grid
  columnDefs: ColDef[] = [];

  isAdmin = false;
  rowData!: PhoneReport[];
  // ng grid

  constructor(
    public notificationService: NotificationService,
    public service: PhoneReportService,
    public userService: UsersService,
    public modal: ModalService,
    private localStorage: LocalStorageService,
    private router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.isAdmin = this.userService.authUser?.role === Role.ADMIN;

    this.buildColumnDefs(this.isAdmin);
    this.loading = true;
    this.actionSubscription = this.service.action$.subscribe(action => {
      this.onAction(action);
    });
    this.columnChange$.pipe(debounceTime(1000)).subscribe(() => {
      const currentColumnState = this.gridApi.getColumnState();

      this.localStorage.set(`phone_report_table_state_${this.userService.authUser.id}`, currentColumnState);
    });
    this.paginationPageSize = this.localStorage.get(`phone_report_table_component_size_${this.userService.authUser.id}`) || 20;

    interval(60_000)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.loading)
      )
      .subscribe(() => this.update());
  }

  onAction(event: PhoneReportActionData) {
    this.actions.emit(event);
    if (event.name === 'delete') {
      const dialogRef = this.modal.alert().size('sm').component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.delete(event.row);
        }
      });
    }
  }

  update() {
    this.loading = true;
    this.requestParams.size = this.paginationPageSize;
    this.requestParams.page = this.currentPageNumber - 1;
    this.requestParams.sort = this.sortState;
    this.resetBatch();

    this.service.all(this.requestParams).subscribe({
      next: (collection: PhoneReportCollection) => {
        this.rowData = collection.content;
        this.totalRowsCount = collection.totalElements;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Phone reports',
          message: 'An error occurred while loading the reports',
          serviceName: 'API GATEWAY',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      },
    });
  }

  navigateToTestResults(report: PhoneReport) {
    if (!report) {
      return;
    }

    const route = '/live-testing';

    this.router.navigate([route], {
      queryParams: { phoneNumber: report.phone },
    });
  }

  delete(report: PhoneReport) {
    this.loading = true;
    this.service.delete(report.id).subscribe({
      next: () => {
        this.notificationService.success('Report removed', 'Phone reports');
        this.update();
      },
      error: error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Phone reports',
          message: 'An error occurred while loading the reports',
          serviceName: 'API GATEWAY',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
      },
    });
  }

  onCellClick(event: CellClickEvent) {
    if (event.column.prop === 'description') {
      this.modal.alert().size('sm').isBlocking(false).title('Description Report').body(BrowserUtils.escapeHtml(event.row.data.description)).open();
    }
  }

  formatDate(date) {
    return moment(date).format('DD/MM/YY HH:mm:ss').split(' ');
  }

  formatUser(row) {
    return row.userEmail + '<' + row.userFullName + '>';
  }

  changeSize($event, size) {
    this.paginationPageSize = size;
    this.localStorage.set(`phone_report_table_component_size_${this.userService.authUser.id}`, size);
    this.update();
  }

  resetBatch() {
    this.batchSelectedIds.clear();
    this.gridApi.deselectAll();
  }

  getRowId = (params: { data: PhoneReport }) => params.data.id;

  // ng grid ...
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.update();
    const columnState = this.localStorage.get(`phone_report_table_state_${this.userService.authUser.id}`) as ColumnState[];
    const finalColumnState = this.getFinalColumnState(columnState, this.isAdmin);
    this.gridApi.setGridOption('columnDefs', this.buildColumnDefs(this.isAdmin));
    this.gridApi.applyColumnState({ state: finalColumnState, applyOrder: true });
  }

  onRowSelected() {
    const selectedRows = this.gridApi.getSelectedRows();
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }

  ngOnDestroy(): void {
    this.actionSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildColumnDefs(isAdmin: boolean) {
    const batchColumn = {
      headerName: '',
      field: 'batch',
      checkboxSelection: true,
      maxWidth: 40,
      headerCheckboxSelection: true,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
    };
    const idColumn = {
      headerName: 'ID',
      field: 'id',
      colId: 'id',
      maxWidth: 80,
      minWidth: 80,
      sortable: true,
      comparator: () => 0,
      initialHide: true,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.id}</div>`;
      },
    };

    const testIdColumn = {
      headerName: 'Test ID',
      field: 'testId',
      colId: 'testId',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      width: 90,
      initialWidth: 90,
      maxWidth: 100,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const testId = params.data.testId || '';
        return `<div class="one-line">${testId}</div>`;
      },
    };

    const testTypeColumn = {
      headerName: 'Test Type',
      field: 'testType',
      colId: 'testType',
      maxWidth: 80,
      minWidth: 80,
      hide: true,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const testType = params.data.testType || '';
        let label = '';

        switch (testType) {
          case PhoneReportTestType.LNT:
            label = 'LNT';
            break;
          case PhoneReportTestType.MNT:
            label = 'MNT';
            break;
          default:
            label = '';
        }

        return `<div class="one-line">${label}</div>`;
      },
    };

    const dateColumn = {
      headerName: 'Date',
      field: 'createdAt',
      colId: 'createdAt',
      sortable: true,
      comparator: () => 0,
      width: 80,
      initialWidth: 80,
      maxWidth: 90,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        const formattedDate = this.formatDate(_.createdAt);

        return `<div class="two-lines"><span>${formattedDate[1]} <br/>${formattedDate[0]}</span></div>`;
      },
    };

    const userEmailColumn = {
      headerName: 'User',
      field: 'userEmail',
      colId: 'userEmail',
      initialWidth: 220,
      width: 220,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.userEmail}</div>`;
      },
    };

    const usernameColumn = {
      headerName: 'Username',
      field: 'username',
      colId: 'username',
      initialWidth: 220,
      width: 220,
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.username}</div>`;
      },
    };

    const companyColumn = {
      headerName: 'Company',
      field: 'companyName',
      colId: 'companyName',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      hide: !isAdmin,
      suppressColumnsToolPanel: !isAdmin,
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${_.companyName}</div>`;
      },
    };

    const phoneColumn = {
      headerName: 'Phone',
      field: 'phone',
      colId: 'phone',
      sortable: true,
      initialWidth: 125,
      width: 125,
      maxWidth: 130,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellClass: 'd-flex justify-content-start align-items-center',
      onCellClicked: params => this.navigateToTestResults(params.data),
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line clickable-wrapper w-fit">${_.phone}</div>`;
      },
    };

    const issueColumn = {
      headerName: 'Issue',
      field: 'issue',
      colId: 'issue',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data;
        return `<div class="one-line">${this.service.issues[_.issue]}</div>`;
      },
    };

    const dlrRecStatusColumn = {
      headerName: 'Internal Test Status',
      field: 'dlrRecStatus',
      colId: 'dlrRecStatus',
      minWidth: 100,
      width: 100,
      maxWidth: 125,
      headerTooltip:
        'Represents DLR and Receipt Statuses of internal test. DLR status - Delivery Report received from the supplier via SMPP, Rec.status - Receipt Status received from our test number.',
      onCellClicked: params => {
        if (params.data.backupTestId === null) return;

        this.testResultModalReportId = params.data.id;
        this.modal.alert().dialogClass('modal-dialog max-large-modal').component(this.phoneReportsTestResultModalTpl).open();
      },
      cellRenderer: (params: ICellRendererParams<PhoneReport>) => {
        const backupTestStatus = params.data.backupTestStatus;
        const backupTestMessageState = params.data.backupTestMessageState;

        if (params.data.backupTestId === null) return `<div title="No test required" class="one-line text-truncate">No test required</div>`;

        return `<div class="d-flex justify-content-center">${DlrRecStatusRenderUtils.formatDlrRecStatus(backupTestStatus, backupTestMessageState, undefined, { class: 'clickable-wrapper fs-3' })}</div>`;
      },
    };

    const connectivityColumn = {
      headerName: 'Connectivity',
      colId: 'connectivity',
      initialWidth: 105,
      width: 105,
      maxWidth: 115,
      sortable: false,
      comparator: () => 0,
      onCellClicked: params => {
        const connectivityInfo = PhoneReportConnectivityUtils.computeConnectivityInfo(params.data);

        if (
          connectivityInfo.status === PhoneReportConnectivityStatus.WARNING &&
          PhoneReportConnectivityUtils.shouldShowConnectivityIssueModal(params.data.issue)
        ) {
          this.connectivityModalInfo = {
            report: params.data,
            connectivityInfo,
            isAdmin: isAdmin,
          };

          this.modal.alert().dialogClass('modal-dialog large-modal').component(this.connectivityModalTpl).open();
        }
      },
      cellRenderer: (params: ICellRendererParams<PhoneReport>) => {
        if (PhoneReportConnectivityUtils.shouldShowConnectivityIssueModal(params.data.issue)) {
          const connectivityInfo = PhoneReportConnectivityUtils.computeConnectivityInfo(params.data);

          const issueLabel = connectivityInfo.description;

          switch (connectivityInfo.status) {
            case PhoneReportConnectivityStatus.COLLECTING:
              return `<div  class="one-line d-flex justify-content-center"><span class="icon-wait far fs-3 spin" title="${connectivityInfo.description}"></span></div>`;
            case PhoneReportConnectivityStatus.OK:
              return `<div class="one-line d-flex justify-content-center"><span class="icon-positive far fs-3 text-success-s2"></span></div>`;
            case PhoneReportConnectivityStatus.WARNING:
              return `<div class="one-line d-flex justify-content-center"><span class="icon-attention far fs-3 text-accent-a2" title="${issueLabel}"></span></div>`;
            default:
              return null;
          }
        } else {
          return `<div class="one-line d-flex justify-content-center"><span >-</span></div>`;
        }
      },
    };

    const descriptionColumn = {
      headerName: 'Description',
      field: 'description',
      colId: 'description',
      sortable: true,
      initialWidth: 270,
      width: 270,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams) => {
        const _ = params.data.description;
        return `<div class="two-lines">${_ ? _ : ''}</div>`;
      },
    };

    const actionsColumn = {
      headerName: 'Actions',
      field: 'actions',
      colId: 'actions',
      maxWidth: 100,
      minWidth: 100,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      tooltipField: 'actions',
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRendererParams: {
        isDeleteReportDisabled: !isAdmin,
      },
      cellRenderer: PhonesReportsTableActionsComponent,
      onCellClicked: (event: CellClickedEvent) => this.onActionClick(event),
    };

    const reportOutcomeColumn: ColDef = {
      headerName: 'Report Outcome',
      field: 'outcomeResolution',
      colId: 'outcomeResolution',
      sortable: false,
      initialWidth: 270,
      width: 270,
      comparator: () => 0,
      initialSort: null,
      onCellClicked: params => {
        const isRowClickable = PhoneReportUtils.isReportOutcomeClickable(isAdmin, params.data);
        if (isRowClickable) {
          this.reportOutcomeModalInfo = params.data;

          this.modal.alert().dialogClass('modal-dialog max-large-modal').component(this.phoneReportsReportOutcomeModalTpl).open();
        }
      },
      cellClass: params => {
        return PhoneReportUtils.isReportOutcomeClickable(isAdmin, params.data) ? 'cp' : '';
      },
      cellRenderer: (params: ICellRendererParams) => {
        const outcomeResolution = params.data.outcomeResolution;
        const outcomeLabel = PhoneReportUtils.getReportOutcomeResolutionLabel(outcomeResolution) || '—';

        const cellContent: Record<PhoneReportOutcomeResolutionEnum, string> = {
          [PhoneReportOutcomeResolutionEnum.UNDER_REVIEW]: `<div class="d-flex align-items-center gap-0_5 report-outcome-cell"><span class="icon-wait spin fs-3" title="${outcomeLabel}"></span> <span>${outcomeLabel}</span></div>`,
          [PhoneReportOutcomeResolutionEnum.EXTERNAL_NUMBER]: `<div class="d-flex align-items-center gap-0_5 report-outcome-cell"><span class="icon-attention text-accent-a2 fs-3" title="${outcomeLabel}"></span> <span>${outcomeLabel}</span></div>`,
          [PhoneReportOutcomeResolutionEnum.GHOST_PROBLEM]: `<div class="d-flex align-items-center gap-0_5 report-outcome-cell"><span class="icon-attention text-accent-a2 fs-3" title="${outcomeLabel}"></span> <span>${outcomeLabel}</span></div>`,
          [PhoneReportOutcomeResolutionEnum.NETWORK_ISSUE]: `<div class="d-flex align-items-center gap-0_5 report-outcome-cell"><span class="icon-attention text-accent-a2 fs-3" title="${outcomeLabel}"></span> <span>${outcomeLabel}</span></div>`,
          [PhoneReportOutcomeResolutionEnum.NO_ISSUE]: `<div class="d-flex align-items-center gap-0_5 report-outcome-cell"><span class="icon-positive text-success-s2 fs-3" title="${outcomeLabel}"></span> <span>${outcomeLabel}</span></div>`,
          [PhoneReportOutcomeResolutionEnum.NUMBER_ISSUE]: `<div class="d-flex align-items-center gap-0_5 report-outcome-cell"><span class="icon-attention text-accent-a2 fs-3" title="${outcomeLabel}"></span> <span>${outcomeLabel}</span></div>`,
        };

        const isClickable = PhoneReportUtils.isReportOutcomeClickable(isAdmin, params.data);
        const cellClass = isClickable && outcomeResolution ? 'clickable-wrapper' : '';

        return `<div class="two-lines w-fit mx-auto ${cellClass}">${cellContent[outcomeResolution] || '—'}</div>`;
      },
    };

    if (isAdmin) {
      return [
        batchColumn,
        testIdColumn,
        dateColumn,
        usernameColumn,
        companyColumn,
        phoneColumn,
        issueColumn,
        dlrRecStatusColumn,
        reportOutcomeColumn,
        {
          ...idColumn,
          initialHide: true,
        },
        {
          ...testTypeColumn,
          initialHide: true,
        },
        {
          ...userEmailColumn,
          initialHide: true,
        },
        {
          ...connectivityColumn,
          initialHide: true,
        },
        {
          ...descriptionColumn,
          initialHide: true,
        },

        actionsColumn,
      ] as ColDef[];
    } else {
      return [
        testIdColumn,
        dateColumn,
        phoneColumn,
        issueColumn,
        descriptionColumn,
        reportOutcomeColumn,
        {
          ...idColumn,
          initialHide: true,
        },
        {
          ...testTypeColumn,
          initialHide: true,
        },
        {
          ...userEmailColumn,
          initialHide: true,
        },

        actionsColumn,
      ] as ColDef[];
    }
  }

  onReportOutcomeUpdate({ outcomeResolution, outcomeDescription, id }: PhoneReportOutcomeOutput) {
    const rowNode = this.gridApi.getRowNode(String(id));
    const updatedReport = { ...(rowNode?.data || this.reportOutcomeModalInfo), outcomeResolution, outcomeDescription };
    this.reportOutcomeModalInfo = updatedReport;
    if (!rowNode) return this.update();

    rowNode.updateData(updatedReport);
  }

  private getFinalColumnState(state: ColumnState[] | null, isAdmin: boolean): ColumnState[] {
    if (!Array.isArray(state) || isAdmin) return state;

    return state.filter(s => s.colId !== 'companyName');
  }
}
